import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import { GET_STOCK_DETAILS, GET_STOCK_POSITION, SEARCH_STOCKS } from '../graphql/queries/stocks';
import PositionDetails from '../components/PositionDetails';
import ExitStrategySection from '../components/ExitStrategySection';
import ExitStrategyModal from '../components/ExitStrategyModal';
import InvalidationStrategySection from '../components/InvalidationStrategySection';
import { parseFloatFromString } from '../utils/formatters';
import StockPriceChart from '../components/StockPriceChart';
import TokenPriceChanges from '../components/TokenPriceChanges';
import StockTransactions from '../components/StockTransactions';
import StockAccountingWarning from '../components/StockAccountingWarning';
import TagButton from '../components/tags/TagButton';
import TagModal from '../components/tags/TagModal';
import {
  formatChangedPercentage,
  formatCurrencyReadable,
  formatNumberToReadable,
  formatPrice,
} from '../utils/formatters';
import PriceUpdateInfo from '../components/PriceUpdateInfo';
import StockMarketHours from '../components/StockMarketHours';
import { usePortfolioContext } from '../contexts/PortfolioContext';

const hasValue = (value) => value != null && String(value).trim() !== '';

const InstrumentMetric = ({ label, value }) => hasValue(value) ? (
  <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm">
    <dt className="shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="min-w-0 break-words text-right font-medium text-gray-900 dark:text-gray-100">
      {value}
    </dd>
  </div>
) : null;

const StockPage = () => {
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const { stockIdentifier } = useParams();
  const location = useLocation();
  const displayIdentifier = (stockIdentifier || '').toUpperCase();
  const stateStock = location.state?.stock;
  const [resolvedStockId, setResolvedStockId] = useState(stateStock?.stockId || null);
  const [transactionRevision, setTransactionRevision] = useState(0);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [exitStrategyModalOpen, setExitStrategyModalOpen] = useState(false);

  useEffect(() => {
    setExitStrategyModalOpen(false);
    setTagModalOpen(false);
  }, [stockIdentifier, currentPortfolioId, currentPortfolio?.currency]);

  useEffect(() => {
    setResolvedStockId(stateStock?.stockId || null);
  }, [displayIdentifier, stateStock?.stockId]);
  const { data, loading, error } = useQuery(SEARCH_STOCKS, {
    variables: { query: displayIdentifier },
    skip: !displayIdentifier || Boolean(stateStock),
    fetchPolicy: 'cache-first',
  });
  const searchedStock = data?.searchStocks?.find(
    (stock) => stock.symbol.toUpperCase() === displayIdentifier,
  );
  const searchStock = stateStock || searchedStock;
  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useQuery(GET_STOCK_DETAILS, {
    variables: {
      symbol: searchStock?.symbol || displayIdentifier,
      stockId: resolvedStockId || searchStock?.stockId || null,
      portfolioId: currentPortfolioId || null,
    },
    skip: !displayIdentifier,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const details = detailsData?.stockDetails;
  // Quote/FX field errors do not invalidate a fresh calendar snapshot. A failed
  // network request or whole-query error must not bless cached asOf as fresh.
  const quoteErrorsOnly = Boolean(detailsError?.graphQLErrors?.length)
    && detailsError.graphQLErrors.every(({ path }) => path?.[0] === 'stockDetails' && path?.[1] === 'quote');
  const marketHoursFresh = !detailsLoading && !detailsError?.networkError
    && (!detailsError || quoteErrorsOnly);
  const stock = details || searchStock
    ? {
        ...searchStock,
        ...details,
        stockId: resolvedStockId || searchStock?.stockId || null,
        sourceProvider: details?.dataProvider || searchStock?.sourceProvider,
      }
    : null;
  const quote = stock?.quote;
  const positionStockId = stock?.stockId;
  const {
    data: positionData,
    loading: positionLoading,
    error: positionError,
    refetch: refetchPosition,
  } = useQuery(GET_STOCK_POSITION, {
    variables: { stockId: positionStockId, portfolioId: currentPortfolioId },
    skip: !positionStockId || !currentPortfolioId,
    fetchPolicy: 'cache-and-network',
  });
  const position = positionStockId ? positionData?.stockPosition : null;
  // The virtual token is priced in portfolio currency; native quotes belong
  // only in the instrument header and transaction entry, never the planner.
  const strategyToken = position?.token ? {
    ...position.token,
    id: positionStockId,
    name: stock?.name,
    ticker: stock?.symbol,
  } : null;
  const canPlan = Boolean(strategyToken && !positionError
    && parseFloatFromString(position?.holdingsAmount) > 0);

  useEffect(() => {
    if (positionStockId && currentPortfolioId && currentPortfolio?.currency) {
      refetchPosition();
    }
  }, [positionStockId, currentPortfolioId, currentPortfolio?.currency, refetchPosition]);

  const hasPosition = (position?.transactions || []).some((tx) => tx?.hidden !== true);
  const accountingUnavailable = position?.transactions?.some((tx) => tx.accountingWarning);
  const quoteCurrency = quote?.currency || stock?.currency;
  const portfolioCurrency = currentPortfolio?.currency;
  const showPortfolioPrice = hasValue(quote?.portfolioPrice)
    && portfolioCurrency && quoteCurrency
    && portfolioCurrency.toUpperCase() !== quoteCurrency.toUpperCase();
  const priceChanges = [
    { label: '1d', value: quote?.changePercent },
    { label: '7d', value: quote?.changePercent7d },
    { label: '30d', value: quote?.changePercent30d },
    { label: '1y', value: quote?.changePercent1y },
  ].filter(({ value }) => hasValue(value)).map(({ label, value }) => ({
    label,
    value: formatChangedPercentage(value),
  }));
  const metrics = [
    { label: 'ISIN', value: stock?.isin },
    { label: 'MIC', value: stock?.mic },
    { label: 'Exchange', value: stock?.exchange },
    { label: 'Sector', value: stock?.sector },
    { label: 'Industry', value: stock?.industry },
    { label: 'Market cap', value: hasValue(quote?.marketCap) ? formatCurrencyReadable(quote.marketCap, quoteCurrency) : null },
    { label: 'Volume', value: hasValue(quote?.volume) ? formatNumberToReadable(quote.volume) : null },
    { label: 'Previous close', value: hasValue(quote?.previousClose) ? formatPrice(quote.previousClose, quoteCurrency) : null },
    {
      label: 'Day range',
      value: hasValue(quote?.dayLow) && hasValue(quote?.dayHigh)
        ? `${formatPrice(quote.dayLow, quoteCurrency)} – ${formatPrice(quote.dayHigh, quoteCurrency)}`
        : null,
    },
    {
      label: '52-week range',
      value: hasValue(quote?.yearLow) && hasValue(quote?.yearHigh)
        ? `${formatPrice(quote.yearLow, quoteCurrency)} – ${formatPrice(quote.yearHigh, quoteCurrency)}`
        : null,
    },
  ].filter(({ value }) => hasValue(value));

  if ((loading || detailsLoading) && !stock) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if ((error || detailsError) && !stock) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
        <strong className="font-bold">Unable to load instrument.</strong>
        <span className="block sm:inline"> {(error || detailsError).message}</span>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Instrument not found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          No stock, ETF, or fund matching <span className="font-mono">{displayIdentifier}</span>.
        </p>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('open-search'))}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
        >
          Search instruments
        </button>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
          <section aria-label="Instrument details" className="min-w-0 lg:col-span-1 lg:col-start-1 lg:row-start-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="flex min-w-0 flex-auto items-center gap-2">
                {hasValue(stock.logoUrl) && (
                  <img
                    src={stock.logoUrl}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded bg-white object-contain p-1"
                  />
                )}
                <h1 className="min-w-0 break-words text-xl font-bold leading-tight text-gray-900 dark:text-gray-100 sm:text-2xl">
                  {stock.name} ({stock.symbol})
                  {hasValue(stock.instrumentType) && (
                    <span className="ml-2 inline-block rounded bg-gray-200 px-1.5 py-0.5 align-middle text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {stock.instrumentType}
                    </span>
                  )}
                </h1>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {hasValue(stock.websiteUrl) && (
                  <a
                    href={stock.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${stock.name} website`}
                    title="Website"
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                  >
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5">
                      <circle cx="12" cy="12" r="9" />
                      <ellipse cx="12" cy="12" rx="4" ry="9" />
                      <path d="M3 12h18M5 6.5h14M5 17.5h14" />
                    </svg>
                  </a>
                )}
                <a
                  href={`https://finance.yahoo.com/quote/${encodeURIComponent(stock.providerSymbol || stock.symbol)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${stock.symbol} on Yahoo Finance`}
                  title="Yahoo Finance"
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-purple-700 hover:bg-gray-200 dark:text-purple-400 dark:hover:bg-gray-700"
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path d="M2 5h4.5l3 5.5L12.5 5H17l-6 10v5H7v-5L2 5Zm17 0h4l-3 10h-3l2-10Zm-2 12h4l-1 3h-4l1-3Z" />
                  </svg>
                </a>
              </div>
            </div>

            {(hasValue(quote?.price) || priceChanges.length > 0) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                {hasValue(quote?.price) && (
                  <div className="flex min-w-0 max-w-full grow flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      {formatPrice(quote.price, quoteCurrency)}
                    </span>
                    {showPortfolioPrice && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ≈ <span className="font-medium">{formatPrice(quote.portfolioPrice, portfolioCurrency)}</span>
                      </span>
                    )}
                    <PriceUpdateInfo timestamp={quote.dataTimestamp} />
                  </div>
                )}
                {priceChanges.length > 0 && (
                  <div className="max-w-full">
                    <TokenPriceChanges items={priceChanges} />
                  </div>
                )}
              </div>
            )}

            {stock.marketHours && (
              <div className="mt-2">
                <StockMarketHours
                  key={stock.stockId || stock.id || stock.symbol}
                  marketHours={stock.marketHours}
                  exchange={stock.exchange}
                  isFresh={marketHoursFresh}
                />
              </div>
            )}
            {!stock.stockId && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Browse-only instrument. It will be imported when you record the first transaction.
              </p>
            )}
            {metrics.length > 0 && (
              <dl aria-label="Instrument metrics" className="mt-3 space-y-1.5 rounded-md bg-gray-50 p-2.5 dark:bg-gray-700/40">
                {metrics.map(({ label, value }) => <InstrumentMetric key={label} label={label} value={value} />)}
              </dl>
            )}
            {hasValue(stock.description) && (
              <details className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                <summary className="cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">About {stock.name}</summary>
                <p className="mt-2 break-words leading-relaxed">{stock.description}</p>
              </details>
            )}
          </section>

          <section className="lg:col-span-1 lg:col-start-1 lg:row-start-2">
            {hasPosition && (
              <div className="mb-3 min-w-0 max-w-full">
                <TagButton tags={position.tags} onClick={() => setTagModalOpen(true)} />
              </div>
            )}
            {accountingUnavailable ? <StockAccountingWarning /> : hasPosition ? (
              <PositionDetails position={position} token={{ ticker: stock.symbol }} />
            ) : (
              <div className="rounded-lg bg-white dark:bg-gray-900/40 p-3">
                <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Current holdings
                </h2>
                {positionError ? (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    Unable to load your position: {positionError.message}
                  </p>
                ) : positionLoading ? (
                  <div className="mt-2 h-6 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                ) : (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    No {stock.symbol} in this portfolio yet. Record a buy below to track value, cost basis and P&amp;L.
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="md:col-span-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:row-span-2 min-h-72 rounded-lg bg-white dark:bg-gray-900/40 p-3">
            <StockPriceChart key={`${stock.stockId || stock.id}:${transactionRevision}`} stock={stock} position={position} />
          </section>
        </div>
      </div>

      {hasPosition && (
        <TagModal
          isOpen={tagModalOpen}
          onClose={() => setTagModalOpen(false)}
          positionId={position.id}
          currentTags={position.tags || []}
          onTagsChanged={refetchPosition}
          isStock
        />
      )}

      {canPlan && !accountingUnavailable && (
        <React.Fragment key={`${positionStockId}:${currentPortfolioId}:${portfolioCurrency}`}>
          <ExitStrategySection
            assetKind="stock"
            position={position}
            token={strategyToken}
            portfolioId={currentPortfolioId}
            refetch={refetchPosition}
            onEdit={() => setExitStrategyModalOpen(true)}
            currency={portfolioCurrency}
          />
          <InvalidationStrategySection
            assetKind="stock"
            position={position}
            token={strategyToken}
            portfolioId={currentPortfolioId}
            refetch={refetchPosition}
          />
          {exitStrategyModalOpen && (
            <ExitStrategyModal
              assetKind="stock"
              isOpen
              onClose={() => setExitStrategyModalOpen(false)}
              position={position}
              token={strategyToken}
              portfolioId={currentPortfolioId}
              refetch={refetchPosition}
            />
          )}
        </React.Fragment>
      )}

      <StockTransactions
        stock={stock}
        portfolioId={currentPortfolioId}
        currentPrice={quote?.price}
        portfolioCurrentPrice={position?.token?.marketData?.currentPrice}
        onImported={async (stockId) => {
          setResolvedStockId(stockId);
          await refetchDetails({ symbol: stock.symbol, stockId });
          setTransactionRevision((value) => value + 1);
        }}
        onChanged={async () => {
          setTransactionRevision((value) => value + 1);
          if (positionStockId && currentPortfolioId) {
            await refetchPosition();
          }
        }}
      />
    </div>
  );
};

export default StockPage;
